import { useRef, useEffect, useState } from 'react';
import RichText from 'components/shared/richtext';
import Button from 'components/shared/button';
import useIsMobile from 'hooks/utils/useIsMobile';
import BuyingPros from 'components/shared/buyingPros';
import { formatPriceByStoreLocale } from 'lib/shopify';
import SiteLink from 'components/shared/sitelink/sitelink';
import Image from 'components/shared/image/image';
import { useRouter } from 'next/router';
import { addLocaleToHref } from 'lib/utils/helpers';
import cn from 'classnames';
import WishlistButton from 'components/shared/wishlist-button';
import { useGlobalModules } from 'contexts';
import UnitPrice from 'components/shared/unit-price/unitPrice';
import AttributeTag from '../../product/attributeTag/attributeTag';
import styles from './productBundleInfo.module.scss';
import BundleCartSection from '../bundleCartSection/bundleCartSection';
import Subscription from '../../product/subscription/subscription';

const ProductBundleInfo = ({ locale, bundlePrices, productBundleData }) => {
  const {
    title,
    description,
    productAttributes,
    productTags,
    linkToExternalWebshop,
    claimsLinks,
    shopifyData,
    size,
  } = productBundleData;

  const isMobile = useIsMobile();
  const stickyCartSectionRef = useRef(null);
  const cartSectionRef = useRef(null);
  const [stickyCartSectionVisible, setStickyCartSectionVisible] = useState(false);
  const [sellingPlan, setSellingPlan] = useState(null);
  const router = useRouter();
  const globalModules = useGlobalModules();

  const { findASalonButton, contactUsButton } = globalModules.buttons.pdp;
  const { buyingPros } = globalModules.sections.pdp;
  const { productsSectionTitle } = globalModules.translations.bundle;

  const {
    onlyAvailableAtSalon,
    outOfStock: outOfStockMessage,
    dontShipToDestination,
    thisProductIs,
  } = globalModules.translations.product;
  const { hasWebshop, hasSalonFinder } = globalModules.settings.locale;
  const { saleText } = globalModules.translations.label;

  const isUnclickable = ({ slug }) => {
    const forbiddenWords = ['keune-stieglitz-giftbox', 'so-pure-box'];
    return forbiddenWords.some(word => slug.includes(word));
  };

  useEffect(() => {
    document.body.style.paddingBottom = isMobile && hasWebshop ? '6rem' : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isMobile, hasWebshop]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 900 && cartSectionRef.current) {
        const observer = new IntersectionObserver(([entry]) => {
          const { isIntersecting } = entry;
          setStickyCartSectionVisible(!isIntersecting);
        });

        observer.observe(cartSectionRef.current);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const AlternativeButtons = () => (
    <>
      {hasSalonFinder && !hasWebshop && !linkToExternalWebshop?.externalLink && (
        <>
          <p className={styles.onlyAvailableAtSalonText}>{onlyAvailableAtSalon}</p>
          <Button locale={locale} className={styles.findASalonButton} button={findASalonButton} />
        </>
      )}
      {!hasSalonFinder && !hasWebshop && !linkToExternalWebshop?.externalLink && (
        <>
          <p className={styles.dontShipToDestinationText}>{dontShipToDestination}</p>
          <Button locale={locale} className={styles.contactUsButton} button={contactUsButton} />
        </>
      )}
      {!hasWebshop && linkToExternalWebshop?.externalLink && (
        <>
          <p className={styles.dontShipToDestinationText}>{dontShipToDestination}</p>
          <Button
            locale={locale}
            className={styles.findASalonButton}
            button={linkToExternalWebshop}
          />
        </>
      )}
    </>
  );

  return (
    <div>
      {hasWebshop && !bundlePrices.outOfStock && (
        <div className={styles.productTagsRow}>
          {!!bundlePrices.compareAtPrice && bundlePrices.salePercentage > 0 && (
            <div className={styles.salesTag}>
              {`${saleText || ''} ${bundlePrices.salePercentage} %`}
            </div>
          )}
          {productTags?.map(tag => (
            <div key={`Product tag ${tag}`} className={styles.productTag}>
              {tag}
            </div>
          ))}
        </div>
      )}
      {hasWebshop && bundlePrices.outOfStock && (
        <div className={styles.productTagsRow}>
          <div className={styles.outOfStockTag}>{outOfStockMessage}</div>
        </div>
      )}

      <div className={styles.productHeader}>
        <div className={styles.productHeaderContainer}>
          <h1 className={styles.productName}>{title}</h1>
          {hasWebshop && !isMobile && (
            <WishlistButton
              className={styles.wishlistIcon}
              productData={productBundleData}
              shopifyData={shopifyData}
            />
          )}
        </div>
        <div className={styles.priceRow}>
          {hasWebshop && bundlePrices && (
            <div className={styles.productPrice}>
              {formatPriceByStoreLocale({ locale, price: bundlePrices.price })}
            </div>
          )}
          {isMobile && hasWebshop && (
            <UnitPrice
              unitPrice={productBundleData.shopifyData?.variants?.[0]?.unitPrice}
              price={bundlePrices.price}
              locale={locale}
              className={styles.unitPrice}
              size={size}
            />
          )}
        </div>
        {isMobile && hasWebshop && (
          <div ref={cartSectionRef}>
            <BundleCartSection
              outOfStock={bundlePrices.outOfStock}
              shopifyData={shopifyData}
              productBundleData={productBundleData}
              locale={locale}
              hideSelect
            />
          </div>
        )}
        {isMobile && <AlternativeButtons />}
      </div>

      {!isMobile && <div className="wae-rating" />}

      <RichText className={styles.productDescription} text={description.json} />
      <div className={styles.productAttributes}>
        {productAttributes?.map(attribute => (
          <AttributeTag
            className={styles.attributeTag}
            key={attribute}
            link={{ title: attribute }}
            locale={locale}
          />
        ))}
      </div>

      <div className={styles.divider} />

      {/* <BundleVariations locale={locale} productBundleData={productBundleData} />
      <UnitPrice price={bundlePrices.price} locale={locale} className={styles.unitPrice} />
      <div className={styles.divider} /> */}

      {/* {bundleSlogan && (
        <div className={styles.variantTitleAndEAN}>
          <p className={styles.singleVariant}>{bundleSlogan}</p>
        </div>
      )} */}

      <Subscription
        shopifyData={shopifyData}
        locale={locale}
        sellingPlan={sellingPlan}
        setSellingPlan={setSellingPlan}
      />

      <div className={styles.productsSection}>
        <h2 className={styles.productsSectionTitle}>{productsSectionTitle}</h2>
        <div className={styles.productsSectionContainer}>
          {productBundleData.products.map(product => (
            <SiteLink
              key={product.slug}
              locale={locale}
              href={addLocaleToHref(`p/${product.slug}`, locale, router)}
              className={cn(
                styles.singleProduct,
                isUnclickable(product) && styles.pointerEventNone
              )}
            >
              <Image
                src={product.thumbnailImage}
                className={styles.singleProductImage}
                layout="fill"
              />
            </SiteLink>
          ))}
        </div>
      </div>

      {!isMobile && hasWebshop && (
        <>
          <div className={styles.divider} />
          <UnitPrice
            unitPrice={productBundleData.shopifyData?.variants?.[0]?.unitPrice}
            price={bundlePrices.price}
            locale={locale}
            className={styles.unitPrice}
            size={size}
          />
        </>
      )}

      <div ref={stickyCartSectionRef}>
        {isMobile && (
          <>
            {!hasWebshop && linkToExternalWebshop?.externalLink && (
              <>
                <p className={styles.dontShipToDestinationText}>{dontShipToDestination}</p>
                <Button
                  locale={locale}
                  className={styles.findASalonButton}
                  button={linkToExternalWebshop}
                />
              </>
            )}
          </>
        )}
      </div>

      <div ref={stickyCartSectionRef}>
        {hasWebshop && (
          <div
            className={cn({
              [styles.fixedCartSectionWrapper]: isMobile,
              [styles.fixedCartSectionWrapperVisible]: isMobile && stickyCartSectionVisible,
            })}
          >
            <BundleCartSection
              outOfStock={bundlePrices.outOfStock}
              className={isMobile && styles.fixedCartSection}
              shopifyData={shopifyData}
              productBundleData={productBundleData}
              locale={locale}
            />
          </div>
        )}
        {!isMobile && <AlternativeButtons />}
      </div>

      {buyingPros[0] && <BuyingPros data={buyingPros[0]} className={styles.buyingPros} />}

      {claimsLinks?.length > 0 && (
        <>
          <h2 className={styles.productAttributesTitle}>{thisProductIs}</h2>
          <div className={styles.productAttributes}>
            {claimsLinks.map(link => (
              <AttributeTag
                className={styles.attributeTag}
                key={link.title}
                link={link}
                locale={locale}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductBundleInfo;
